import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IGetDeliveryTemplateResponse,
  ISetDeliveryTemplateRequest,
} from './types';

export const useGetDeliveryTemplate = (enabled?: boolean, options?: { onNotFound?: () => void; onErrorCallback?: () => void }) => {
  const { responseHandler } = useResponseHandler();
  
  return useQuery<IGetDeliveryTemplateResponse | undefined, DefaultError>({
    queryKey: ['deliveryTemplate'],
    async queryFn() {
      try {
        const response = await apiClient({
          method: 'GET',
          url: 'Configuration/GetDeliveryTemplate',
        });

        const data = await responseHandler<IGetDeliveryTemplateResponse>(response, {
          groupName: 'Configuration - GetDeliveryTemplate',
          onNotFound: options?.onNotFound,
          onError: (status) => {
            if (status === 400 && options?.onErrorCallback) {
              options.onErrorCallback();
            }
          },
        });

        return data;
      } catch (error: any) {
        if (error?.response?.status === 404 || error?.response?.status === 400) {
          if (options?.onNotFound) {
            options.onNotFound();
          }
          return undefined;
        }
        throw error;
      }
    },
    enabled,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useDeleteDeliveryTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteDeliveryTemplate');

  return useMutation<void, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: '',
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: 'Configuration/DeleteDeliveryTemplate',
      });

      return responseHandler(response, {
        groupName: 'useDeleteDeliveryTemplate',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.delete.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostDeliveryTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostDeliveryTemplate');

  return useMutation<void, DefaultError, ISetDeliveryTemplateRequest>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: '',
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Configuration/SetDeliveryTemplate',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostDeliveryTemplate',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
